/**
 * Renderizado: de `kind` + `args` del motor a enunciados en castellano.
 *
 * El motor guarda en el caso las etiquetas ya recortadas (`caso.people[i]`…); aquí se
 * resuelve índice → nombre con el caso e nombre → género/formas con el pack. Así el
 * renderizado funciona igual con un tema recortado o muestreado, y un nombre que no
 * esté en el pack falla en alto en vez de imprimir mal una concordancia.
 */

import type { Case, Clue, ClueKind } from '@murdoku/engine';
import type { NounForms } from './forms';
import { article, nounForms } from './forms';
import type { PackPerson, PackRoom, ThemePack } from './schema';
import type { ClueContext, PersonRef } from './templates';
import { CLUE_TEMPLATES } from './templates';

/** Vista mínima de un caso que necesita el renderizador de pistas. */
export interface CaseView {
  people: readonly string[];
  rooms: readonly string[];
  hours: readonly string[];
}

export interface RenderedClue {
  id: string;
  kind: ClueKind;
  /** Índice de la variante de plantilla usada. */
  variant: number;
  text: string;
}

function lookup<T extends { name: string }>(
  entries: readonly T[],
  name: string | undefined,
  what: string,
  pack: ThemePack,
): T {
  const found = name === undefined ? undefined : entries.find((e) => e.name === name);
  if (found === undefined) {
    throw new Error(`${what} "${String(name)}" no existe en el pack "${pack.id}"`);
  }
  return found;
}

function personRef(
  pack: ThemePack,
  view: CaseView,
  index: number | undefined,
): PersonRef {
  const name = index === undefined ? undefined : view.people[index];
  const person: PackPerson = lookup(pack.people, name, 'persona', pack);
  return { name: person.name, gender: person.gender };
}

function roomForms(
  pack: ThemePack,
  view: CaseView,
  index: number | undefined,
): NounForms {
  const name = index === undefined ? undefined : view.rooms[index];
  const room: PackRoom = lookup(pack.rooms, name, 'sala', pack);
  return nounForms(room);
}

function hourLabel(view: CaseView, index: number | undefined): string {
  const label = index === undefined ? undefined : view.hours[index];
  if (label === undefined) throw new Error(`hora fuera de rango: ${String(index)}`);
  return label;
}

/** "la noche", "la jornada", "el día". */
export function periodPhrase(pack: ThemePack): string {
  return `${article({ gender: pack.period.gender, plural: false })} ${pack.period.noun}`;
}

function contextFor(clue: Clue, view: CaseView, pack: ThemePack): ClueContext {
  const period = periodPhrase(pack);
  const [x, y, z] = clue.args;
  switch (clue.kind) {
    case 'AT':
    case 'NOT_AT':
      return {
        a: personRef(pack, view, x),
        hour: hourLabel(view, y),
        room: roomForms(pack, view, z),
        period,
      };
    case 'OPEN':
    case 'CLOSE':
    case 'FIRST_IN':
    case 'LAST_IN':
      return { a: personRef(pack, view, x), room: roomForms(pack, view, y), period };
    case 'JUST_BEFORE':
    case 'BEFORE':
      return {
        a: personRef(pack, view, x),
        b: personRef(pack, view, y),
        room: roomForms(pack, view, z),
        period,
      };
  }
}

/** Número de variantes disponibles para una clase. */
export function variantCount(kind: ClueKind): number {
  return CLUE_TEMPLATES[kind].length;
}

/**
 * Enunciado de una pista. Sin `variant` explícita se elige de forma determinista a
 * partir del `id` de la pista: el mismo caso siempre se lee igual.
 */
export function renderClue(
  clue: Clue,
  view: CaseView,
  pack: ThemePack,
  variant?: number,
): string {
  const templates = CLUE_TEMPLATES[clue.kind];
  const index = (variant ?? hashString(clue.id)) % templates.length;
  const template = templates[index];
  if (template === undefined)
    throw new Error(`variante fuera de rango: ${String(variant)}`);
  return template(contextFor(clue, view, pack));
}

/**
 * Enunciados de todas las pistas de un caso. Las variantes rotan en round-robin por
 * clase: dos pistas seguidas del mismo tipo nunca comparten formulación (hasta agotar
 * las variantes de la clase).
 */
export function renderClues(
  clues: readonly Clue[],
  view: CaseView,
  pack: ThemePack,
): RenderedClue[] {
  const counters = new Map<ClueKind, number>();
  return clues.map((clue) => {
    const next = counters.get(clue.kind) ?? 0;
    counters.set(clue.kind, next + 1);
    const variant = next % CLUE_TEMPLATES[clue.kind].length;
    return {
      id: clue.id,
      kind: clue.kind,
      variant,
      text: renderClue(clue, view, pack, variant),
    };
  });
}

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
}

/** Rellena `{marcador}` y lanza si el texto pide un marcador que no existe. */
export function interpolate(template: string, values: Record<string, string>): string {
  return template.replace(/\{([a-z_]+)\}/g, (_match, key: string) => {
    const value = values[key];
    if (value === undefined) throw new Error(`marcador sin valor: {${key}}`);
    return value;
  });
}

function crimeValues(caso: Case, pack: ThemePack): Record<string, string> {
  const room = roomForms(pack, caso, caso.crime.room);
  return {
    room_name: room.name,
    room_def: room.def,
    room_en: room.en,
    room_al: room.al,
    room_del: room.del,
    hour: hourLabel(caso, caso.crime.hour),
  };
}

/** Apertura del caso: aparición del cuerpo, sala y hora. Sin spoilers. */
export function renderOpening(caso: Case, pack: ThemePack): string {
  return interpolate(pack.opening, {
    ...crimeValues(caso, pack),
    victim: pack.victim.name,
    victim_desc: pack.victim.descriptor,
  });
}

/**
 * Cierre del caso para una acusación. Con el culpable real devuelve el veredicto de
 * acierto; con cualquier otra persona, el de fallo (que nunca nombra al culpable).
 */
export function renderVerdict(caso: Case, pack: ThemePack, accused: number): string {
  const values = crimeValues(caso, pack);
  if (accused === caso.murderer) {
    const culprit = personRef(pack, caso, accused);
    const descriptor = lookup(pack.people, culprit.name, 'persona', pack).descriptor;
    return interpolate(pack.verdicts.correct, {
      ...values,
      culprit: culprit.name,
      culprit_desc: descriptor,
    });
  }
  const accusedRef = personRef(pack, caso, accused);
  const descriptor = lookup(pack.people, accusedRef.name, 'persona', pack).descriptor;
  return interpolate(pack.verdicts.wrong, {
    ...values,
    accused: accusedRef.name,
    accused_desc: descriptor,
  });
}

export interface RenderedCase {
  title: string;
  tagline: string;
  opening: string;
  ambience: readonly string[];
  clues: RenderedClue[];
}

/** Todo el texto de un caso salvo el veredicto (que depende de la acusación). */
export function renderCase(caso: Case, pack: ThemePack): RenderedCase {
  if (caso.theme !== pack.id) {
    throw new Error(`el caso es del tema "${caso.theme}", no de "${pack.id}"`);
  }
  return {
    title: pack.name,
    tagline: pack.tagline,
    opening: renderOpening(caso, pack),
    ambience: pack.ambience,
    clues: renderClues(caso.clues, caso, pack),
  };
}
